import { find, split } from 'lodash';
import { useMemo } from 'react';

import { META_KEY } from 'constants/meta.contants';
import { PayoutRejectStatusDetails, BooleanEnum } from 'enums';
import { formatUtils } from 'utils';

import { useUserContext } from './use-user-context.hook';

export const useMeta = () => {
  const { meta } = useUserContext();
  const telegramSupportBotUrl = useMemo(
    () => find(meta, { key: META_KEY.TELEGRAM_SUPPORT_BOT_URL })?.value,
    [meta],
  );

  const payoutMaxActiveOrders = useMemo(() => {
    const value = Number(
      find(meta, { key: META_KEY.PAYOUT_MAX_ACTIVE_ORDERS })?.value,
    );
    if (!isNaN(value)) {
      return value;
    }
  }, [meta]);

  const payinRequisitesVerificationRequired = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.PAYIN_REQUISITES_VERIFICATION_REQUIRED,
    })?.value;

    return value === BooleanEnum.True;
  }, [meta]);

  const payinRequisitesVerificationExpirationAfter = useMemo(() => {
    const value = Number(
      find(meta, {
        key: META_KEY.PAYIN_REQUISITES_VERIFICATION_EXPIRATION_AFTER,
      })?.value,
    );
    if (!isNaN(value)) {
      return value;
    }
  }, [meta]);

  const traderCanOpenDispute = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.TRADER_CAN_OPEN_DISPUTE,
    })?.value;

    return value === BooleanEnum.True;
  }, [meta]);

  const traderCanCancelDispute = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.TRADER_CAN_CANCEL_DISPUTE,
    })?.value;

    return value === BooleanEnum.True;
  }, [meta]);

  const traderCanCompleteCancelled = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.TRADER_CAN_COMPLETE_CANCELLED,
    })?.value;

    return value === BooleanEnum.True;
  }, [meta]);

  const ordersMonitoringEnabled = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.ORDERS_MONITORING_ENABLED,
    })?.value;

    return value === BooleanEnum.True;
  }, [meta]);

  const orderAutomationApiHost = useMemo(
    () => find(meta, { key: META_KEY.ORDER_AUTOMATION_API_HOST })?.value,
    [meta],
  );

  const payoutOrderAvailableRejectStatusDetailsList: PayoutRejectStatusDetails[] =
    useMemo(() => {
      const value = find(meta, {
        key: META_KEY.PAYOUT_ORDER_AVAILABLE_REJECT_STATUS_DETAILS_LIST,
      })?.value;

      return value
        ? (split(value, ',').filter(Boolean) as PayoutRejectStatusDetails[])
        : [];
    }, [meta]);

  const isTurboDepositEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.TURBO_DEPOSIT_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isMultitransferEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.MULTITRANSFER_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isCartelHouseSmsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.CARTEL_HOUSE_SMS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isTechProtectionReceiptsValidationEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.TECH_PROTECTION_RECEIPTS_VALIDATION_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isChequeWalesEnabled = useMemo(
    () =>
      find(meta, { key: META_KEY.CHEQUE_WALES_ENABLED })?.value ===
      BooleanEnum.True,
    [meta],
  );

  const isShopWebhookOverridesEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.SHOP_WEBHOOK_OVERRIDES_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isShopTabsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.SHOP_TABS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isUseDeepLinkPaymentPageSettingEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.USE_DEEP_LINK_PAYMENT_PAGE_SETTING_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isIncludeDeeplinkInResponseAllowed = useMemo(
    () =>
      find(meta, {
        key: META_KEY.INCLUDE_DEEPLINK_IN_RESPONSE_ALLOWED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isPayoutVolumeControlEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.PAYOUT_VOLUME_CONTROL_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isP2PProviderCanIgnoreOrderPaymentTypeEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.P2P_PROVIDER_CAN_IGNORE_ORDER_TRADE_METHOD,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const sciAvailableTemplateList = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.SCI_AVAILABLE_TEMPLATE_LIST,
    })?.value;
    return value
      ? split(value, ',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }, [meta]);

  const sciAvailableThemeList = useMemo(() => {
    const value = find(meta, { key: META_KEY.SCI_AVAILABLE_THEME_LIST })?.value;
    return value
      ? split(value, ',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }, [meta]);

  const required2FARoles = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.REQUIRED_2FA_FOR_ROLES,
    })?.value;
    return value ? split(value, ',').filter(Boolean) : [];
  }, [meta]);

  const payinOrderP2POverrideKeys = useMemo(() => {
    const value = find(meta, {
      key: META_KEY.PAYIN_ORDER_P2P_OVERRIDE_KEYS,
    })?.value;
    return formatUtils.parseList(value);
  }, [meta]);

  const isSciCustomerAlternativesEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.SCI_CUSTOMER_ALTERNATIVES_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isClarityEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.CLARITY_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isClarityShopDefaultEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.CLARITY_SHOP_DEFAULT_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isPayoutOrderCascadeTimeoutEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.PAYOUT_ORDER_CASCADE_TIMEOUT_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isMerchantCanCreatePayoutOrderEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.MERCHANT_CAN_CREATE_PAYOUT_ORDER,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isMerchantOperatorsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.MERCHANT_OPERATORS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isOperatorTraderActionsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.OPERATOR_TRADER_ACTIONS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isDynamicAdaptersEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.DYNAMIC_ADAPTERS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isProviderAgentsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.PROVIDER_AGENTS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isTokenIpValidationEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.TOKEN_IP_VALIDATION_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isProviderIntervalCommissionsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.PROVIDER_INTERVAL_COMMISSIONS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isZeroFeesForbidden = useMemo(
    () =>
      find(meta, {
        key: META_KEY.ZERO_FEES_FORBIDDEN,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isPayinProviderConversionStatisticsEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.PAYIN_PROVIDER_CONVERSION_STATISTICS_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isGptTemplateGenerationEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.GPT_TEMPLATE_GENERATION_ENABLED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  const isP2PProviderIgnoreWebhookAllowedEnabled = useMemo(
    () =>
      find(meta, {
        key: META_KEY.P2P_PROVIDER_IGNORE_WEBHOOK_ALLOWED,
      })?.value === BooleanEnum.True,
    [meta],
  );

  return {
    orderAutomationApiHost,
    telegramSupportBotUrl,
    payoutMaxActiveOrders,
    payoutOrderAvailableRejectStatusDetailsList,
    payinRequisitesVerificationRequired,
    payinRequisitesVerificationExpirationAfter,
    traderCanOpenDispute,
    traderCanCancelDispute,
    traderCanCompleteCancelled,
    ordersMonitoringEnabled,
    isTurboDepositEnabled,
    isMultitransferEnabled,
    isCartelHouseSmsEnabled,
    isTechProtectionReceiptsValidationEnabled,
    isChequeWalesEnabled,
    isShopTabsEnabled,
    isShopWebhookOverridesEnabled,
    isUseDeepLinkPaymentPageSettingEnabled,
    isIncludeDeeplinkInResponseAllowed,
    sciAvailableTemplateList,
    sciAvailableThemeList,
    isPayoutVolumeControlEnabled,
    isP2PProviderCanIgnoreOrderPaymentTypeEnabled,
    required2FARoles,
    isPayoutOrderCascadeTimeoutEnabled,
    isMerchantCanCreatePayoutOrderEnabled,
    isMerchantOperatorsEnabled,
    isOperatorTraderActionsEnabled,
    isSciCustomerAlternativesEnabled,
    isClarityEnabled,
    isClarityShopDefaultEnabled,
    isDynamicAdaptersEnabled,
    isProviderAgentsEnabled,
    isProviderIntervalCommissionsEnabled,
    payinOrderP2POverrideKeys,
    isTokenIpValidationEnabled,
    isZeroFeesForbidden,
    isPayinProviderConversionStatisticsEnabled,
    isGptTemplateGenerationEnabled,
    isP2PProviderIgnoreWebhookAllowedEnabled,
  };
};
